import React from "react";
import styled from "styled-components";
import { CaretDown } from "@styled-icons/fa-solid/CaretDown";
import { CaretUp } from "@styled-icons/fa-solid/CaretUp";

// IMPORTANT: Quickly add custom THEMES by adding into THEMES object further down this file.

// StyledComponents - ReactTable
export const StyledTableContainer = styled.div`
  overflow-x: auto;
  /* margin: 24px 0; */
  border-collapse: collapse;
`;

export const StyledTableComponent = styled.div`
  width: 100%;
  position: relative;
  ${(props) => THEMES[props?.tableTheme || "default"]};
`;

export const StyledTable = styled.table`
  box-sizing: border-box;
  th {
    user-select: none;
    position: relative;
  }
  td {
    display: flex;
    align-items: center;
    justify-content: center;
    .cell-content {
      position: relative;
      flex: 1;
    }
    .cell-popover {
      flex: 1;
      overflow-wrap: break-word;
      white-space: normal;
      word-break: break-word;
    }
  }
  // Table row striping
  tr {
    &:nth-child(odd) td {
      ${(props) =>
        props.striped &&
        `
        background: #f7f7f7;
      `};
    }
  }

  ${(props) =>
    props.showRowNumbers &&
    `
      tbody > tr {
        display: flex;
        align-items: center;
      }
    `};
`;

export const RowNumbers = styled.div`
  position: absolute;
  right: 100%;
  padding-right: 12px;
  color: #707070;
  font-size: 12px;
`;

export const StyledTD = styled.td`
  display: flex;
  align-items: center;
  ${(props) =>
    props.centered &&
    `
    justify-content: center;
  `};
`;

export const StyledThSortContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const StyledNoDataComponent = styled.td`
  width: 100%;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  /* padding: 0 !important; */
  background-color: #fff;
  h3 {
    font-size: 17px;
    font-weight: 400;
  }
  p {
    font-size: 13px;
    padding: 11px;
  }
`;

export const TD = ({ centered, children, ...props }) => {
  return (
    <StyledTD centered={centered} {...props}>
      <div className="cell-content">{children}</div>
    </StyledTD>
  );
};

////////////////////////////////
// Border styles
const cellBorder = `1px solid rgba(0, 0, 0, 0.08)`;
const tableBorder = `1px solid #aeb0b5`;

// Table border visibility styles
const borderVisibility = {
  visible: `
    tr {
      border-bottom: ${cellBorder};
      &:last-child {
        border-bottom: 0px;
      }
    }
    th,
    td {
      border-right: ${cellBorder};
      &:last-child {
        border-right: 0px;
      }
    }
    thead {
      border-bottom: 1px solid #a0a0a0;
    }
    ${StyledTableContainer} {
      border: ${tableBorder};
    }
    table {
      border-bottom: ${tableBorder};
    }
  `,
  hidden: `
    ${StyledTableContainer},
    table,
    thead,
    th,
    tr,
    td {
      border: none;
    }
    `,
};

// Text overflow hidden style
const textOverflowHidden = `
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

// ADD NEW CUSTOM STYLES HERE.
// Table themes - padding, positioning, overflow, border visibility
const THEMES = {
  default: `
    ${borderVisibility.visible}
    .cell-content,
    th .sorting-text {
      ${textOverflowHidden}
    }
    td {
      padding: 7px 5px;
    }
    th {
      padding: 5px 7px;
    }
  `,
  genericTheme: `
    ${borderVisibility.visible}
    .cell-content,
    th .sorting-text {
      ${textOverflowHidden}
    }
    tr {
      border-color: #aeb0b5;
    }
    th,
    td {
      border-color: #aeb0b5;
    }
    td {
      padding: 20px;
      font-size: 16px;
    }
    th {
      padding: 12px 20px;
      background: #f7f7f7;
    }
  `,
  casesTableTheme: `
    ${borderVisibility.visible}
    .cell-content {
      ${textOverflowHidden}
      padding: 11px;
      font-size: 16px;
    }
    .cell-popover {
      ${textOverflowHidden}
      padding: 11px;
      font-size: 16px;
    }
    th {
      padding: 11px;
      background: #f7f7f7;
      display: flex;
      align-items: center;
    }
    thead {
      border-bottom-color: rgb(233, 233, 233);
    }
    ${StyledTableContainer} {
      border-color: rgb(233, 233, 233);
    }
    table {
      border-bottom-color: rgb(233, 233, 233);
    }
  `,
  housingSubRowTheme: `
    ${borderVisibility.hidden}
    th,
    td {
      white-space: normal;
      padding: 5px 20px 5px 20px;
      width: auto;
      overflow: auto;
      background: transparent;
    }
    ${StyledNoDataComponent} {
      background-color: transparent;
    }
  `,
  linkageDeletionTheme: `
    ${borderVisibility.visible}
    th {
      padding: 10px;
      border-color: #aeb0b5;
    }
    tr {
      border-color: #e0e0e0;
    }
    tbody > tr {
      border-left: 6px solid red;
    }
    th,
    td {
      border: none;
      padding: 10px;
    }
  `,
};

// StyledComponents - ReactTable Loading UI
export const StyledLoaderOverlay = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  display: grid;
  place-items: center;
  z-index: 10;
  user-select: none;
  animation: fade-in 0.5s ease-in-out;

  @keyframes fade-in {
    0% {
      opacity: 0;
      transform: translateY(-3px);
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
    }
  }
  .loading-text {
    font-size: 18px;
    font-weight: normal;
  }
`;

// UI Component
export const TableLoadingUI = ({
  text = "Loading...",
  children = "Loading...",
  loading = false,
}) => {
  return (
    <>
      {!!loading && (
        <StyledLoaderOverlay $loading={loading}>
          <p className="loading-text">{text || children}</p>
        </StyledLoaderOverlay>
      )}
    </>
  );
};

// ReactTable - Custom useFlexLayout
// react-table's useFlexLayout is terrible and messes up column widths. This simplified version works better
export const useCustomFlexLayout = (hooks) => {
  const getRowStyles = (props, { instance }) => [
    props,
    {
      style: {
        display: "flex",
      },
    },
  ];

  const getHeaderProps = (props, { column }) => [
    props,
    {
      style: {
        flex: column.width,
        minWidth: column.fixedWidth || column.minWidth,
        width: column.fixedWidth || column.width,
        maxWidth: column.fixedWidth || column.maxWidth,
      },
    },
  ];

  const getCellProps = (props, { cell }) => {
    return [
      props,
      {
        style: {
          flex: cell.column.width,
          minWidth: cell.column.fixedWidth || cell.column.minWidth,
          width: cell.column.fixedWidth || cell.column.width,
          maxWidth: cell.column.fixedWidth || cell.column.maxWidth,
        },
      },
    ];
  };

  hooks.getRowProps.push(getRowStyles);
  hooks.getHeaderGroupProps.push(getRowStyles);
  hooks.getHeaderProps.push(getHeaderProps);
  hooks.getCellProps.push(getCellProps);
};

export const ThSortContainer = ({
  isSorted = false,
  isSortedDesc = false,
  iconSize = "1rem",
  children,
}) => {
  // In case we want different arrow "themes" in future
  const arrow = {
    up: <CaretUp size={iconSize} style={{ marginTop: "-5px" }} />,
    down: <CaretDown size={iconSize} style={{ marginTop: "-5px" }} />,
  };

  return (
    <StyledThSortContainer tabIndex={0}>
      <div className="sorting-text">{children}</div>
      {isSorted && <div>{isSortedDesc ? arrow.up : arrow.down}</div>}
    </StyledThSortContainer>
  );
};

// Place inside tbody alongside other data table-row elements
export const NoDataComponent = ({
  text = "No Data Found",
  subText = "",
  children,
  ...props
}) => {
  return (
    <tr>
      <StyledNoDataComponent {...props}>
        <h3>{children || text}</h3>
        {!!subText && <p>{subText}</p>}
      </StyledNoDataComponent>
    </tr>
  );
};
